/* eslint-disable no-param-reassign */
import ko from 'knockout';
import _ from 'underscore';
import pageContext from '@bingads-webui-static-legacy/page-context';

const i18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/campaign-creation-advanced');

const defaultDateFormat = 'M/d/yyyy';

function formatDateToAPIFormat(date) {
  if (date == null) {
    return '';
  }
  return i18n.formatDate(date, {
    raw: defaultDateFormat,
  });
}

function parseAPIDate(dateString) {
  return i18n.parseDate(dateString.substring(0, 10), { raw: 'yyyy-MM-dd' });
}

function convertDateBetweenTimeZones(date, {
  from,
  to,
} = {}) {
  const dateString = i18n.formatDate(date, {
    timeZone: from,
    raw: 'yyyy-MM-dd',
  });

  return i18n.parseDate(dateString, {
    timeZone: to,
    raw: 'yyyy-MM-dd',
  });
}

function convertMachineDateToAccountDate(date) {
  return convertDateBetweenTimeZones(date, { from: null });
}

function convertAccountDateToMachineDate(date) {
  return convertDateBetweenTimeZones(date, { to: null });
}

export default class ViewModel {
  constructor({
    startDate = null,
    endDate = null,
    adGroupStatus = undefined,
    activityLogger,
    callOnChangeCb = _.identity,
    isCreate = undefined,
    allowNoEndDateSelection = true,
    allowStartDateSelection = true,
    allowEndDateSelection = true,
  } = {}) {
    isCreate = _.isUndefined(isCreate) ? _.isNull(startDate) : isCreate;
    //used in datePicker ko binding
    this.culture = pageContext.Culture || 'en-US';
    this.format = pageContext.CultureDateTimeFormat.ShortDatePattern || defaultDateFormat;
    this.datePickerNavigateToFutureAriaLabel = i18n.getString('DatePicker_NavigateToFuture_AriaLabel');
    this.datePickerNavigateToPastAriaLabel = i18n.getString('DatePicker_NavigateToPast_AriaLabel');
    this.activityLogger = activityLogger;
    this.currentDate = convertDateBetweenTimeZones(new Date());
    this.rawStartDate = ko.observable(startDate ? parseAPIDate(startDate) : this.currentDate);
    this.rawEndDate = ko.observable(endDate ? parseAPIDate(endDate) : convertDateBetweenTimeZones(new Date(new Date().setFullYear(new Date().getFullYear() + 1))));
    this.disableNoEndDateSelection = ko.observable(!allowNoEndDateSelection);
    this.disableStartDateSelection = ko.observable(!allowStartDateSelection);
    this.disableEndDateSelection = ko.observable(!allowEndDateSelection);

    // set start date and end date which will be binded to kendo datePicker
    this.startDate = ko.computed({
      read: () => {
        return _.isDate(this.rawStartDate()) ? convertAccountDateToMachineDate(this.rawStartDate()) : null;
      },
      write: (value) => {
        this.rawStartDate(_.isDate(value) ? convertMachineDateToAccountDate(value) : null);
      },
    });

    this.startDate.subscribe(() => {
      if (activityLogger) {
        activityLogger.signal('start date changed', 'ad schedule');
      }
      callOnChangeCb(this.getRawValues());
    });

    this.endDate = ko.computed({
      read: () => {
        return _.isDate(this.rawEndDate()) ? convertAccountDateToMachineDate(this.rawEndDate()) : null;
      },
      write: (value) => {
        this.rawEndDate(_.isDate(value) ? convertMachineDateToAccountDate(value) : null);
      },
    });

    this.endDate.subscribe(() => {
      if (activityLogger) {
        activityLogger.signal('end date changed', 'ad schedule');
      }
      callOnChangeCb({
        ...this.getRawValues(),
        hasFixedStartDate: this.hasFixedStartDate,
      });
    });

    this.hasEndDate = ko.observable(!_.isNull(endDate));

    this.enableEndDateSelection = ko.computed(() => {
      return this.hasEndDate() && !this.disableEndDateSelection();
    });

    /* Start date is editable in below conditions:
      1. create adgroup so startDate is null
      2. start date is later than current day
      3. adgroup status is Draft
    */
    this.hasFixedStartDate = this.disableStartDateSelection() || !(isCreate || (this.rawStartDate() > this.currentDate) || adGroupStatus === 'Draft');
    this.startDateError = ko.observable('');
    this.endDateError = ko.observable('');

    this.hasEndDate.Editing = ko.computed({
      read () {
        return this.hasEndDate() ? 'true' : 'false';
      },
      write (newValue) {
        if (newValue === 'false') {
          this.endDateError('');
        } else {
          this.validateEndDate(this.endDate());
        }

        this.hasEndDate(newValue === 'true');
      },
    }, this);


    this.hasEndDate.subscribe((value) => {
      if (activityLogger) {
        activityLogger.signal(`has end date change: ${value}`, 'ad schedule');
      }
      callOnChangeCb({
        ...this.getRawValues(),
        hasFixedStartDate: this.hasFixedStartDate,
      });
    });

    this.rawStartDate.subscribe(this.validateStartDate, this);

    this.rawEndDate.subscribe(this.validateEndDate, this);
  }

  validateStartDate(date) {
    //check wether date is valid
    if (_.isNull(date)) {
      this.startDateError(i18n.getString('Campaign_Validation_StartDateInvalid'));
    } else {
      // 1. compare date with current date
      this.startDateError(date < this.currentDate ? i18n.getString('Campaign_Validation_StartDateGreaterThanToday') : '');
      // 2. compare date with end date if has end date
      if (this.enableEndDateSelection ) {
        this.endDateError(date > this.rawEndDate() ? i18n.getString('Campaign_Validation_EndDateGreaterThanStartDate') : '');
      }
    }
  }

  validateEndDate(date) {
    if (_.isNull(date)) {
      this.endDateError(i18n.getString('Campaign_Validation_EndDateInvalid'));
    } else {
      // compare end date with start date
      this.endDateError(date < this.rawStartDate() ? i18n.getString('Campaign_Validation_EndDateGreaterThanStartDate') : '');
    }
  }

  getValues() {
    // MT accept date format as 'M/d/yyyy'

    /* API uses current date and '1/1/3000' to unset StartDate & EndDate
       See here: https://msdn.microsoft.com/en-us/library/bing-ads-campaign-management-adgroup */
    return {
      StartDate: formatDateToAPIFormat(this.rawStartDate()),
      EndDate: this.hasEndDate() ? formatDateToAPIFormat(this.rawEndDate()) : '1/1/3000',
    };
  }

  getRawValues() {
    return {
      StartDate: this.rawStartDate(),
      EndDate: this.hasEndDate() ? this.rawEndDate() : null,
    };
  }

  validate() {
    if (this.hasFixedStartDate) {
      return _.isEmpty(this.endDateError());
    }

    if (_.isNull(this.rawStartDate())) {
      this.startDateError(i18n.getString('Campaign_Validation_StartDateInvalid'));
    } else {
      this.startDateError(this.rawStartDate() < this.currentDate ? i18n.getString('Campaign_Validation_StartDateGreaterThanToday') : '');
    }

    if (this.enableEndDateSelection) {
      if (_.isNull(this.rawEndDate())) {
        this.endDateError(i18n.getString('Campaign_Validation_EndDateInvalid'));
      } else {
        this.endDateError(this.rawStartDate() > this.rawEndDate() ? i18n.getString('Campaign_Validation_EndDateGreaterThanStartDate') : '');
      }
    }

    return _.isEmpty(this.startDateError()) && _.isEmpty(this.endDateError());
  }
}
